import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import { ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import useUser from 'utils/useUser';
import NavigationHeader from 'components/molecules/navigation-header';
import classNames from 'classnames';
import { isValidGuid } from 'infrastructure/functions';

import s from './styles.module.scss';
import AppointmentList from './appointment-list';
import { useRpmCcmHeader } from './hooks/use-rpm-ccm-header';
import SearchPatientsByType from '../search-patients-by-type';

import type { INavigationHeaderProps } from 'components/molecules/navigation-header';
import type { FC } from 'react';

interface IProps extends INavigationHeaderProps {
  patientGuid: string;
  module?: ModuleType;
  clinicGuid?: string;
}

const RpmCcmNavigationHeader: FC<IProps> = ({
  patientGuid,
  module = ModuleType.CCM,
  clinicGuid,
  goBackPath,
  title,
}) => {
  const { t } = useTranslation();
  const {
    dataOptions,
    debounceSearch,
    isLoading,
    placeholder,
    options,
    dateRange,
    openPicker,
    DEFAULT_RANGE,
    DEFAULT_FILTER,
    searchValue,
    searchKey,
    onApply,
    setSearchKey,
    setSearchValue,
    setOpenPicker,
    onSelect,
    onCalendarChange,
  } = useRpmCcmHeader({ module, clinicGuid, patientGuid });

  const { isPatient } = useUser();

  return (
    <NavigationHeader
      goBackPath={goBackPath}
      title={title}
      showMobileMiniNavBtn
    >
      <div className={classNames(s.wrapper, { [s['for-patient']]: isPatient })}>
        <p className={s.title}>{t('labels.reportTimeWindow')}:</p>
        <div data-cy="report-time-window" className={s['date-wrapper']}>
          <BaseRangeDatepicker
            onCalendarChange={onCalendarChange}
            panelRender={(children) => (
              <AppointmentList dateRange={dateRange} patientGuid={patientGuid}>
                {children}
              </AppointmentList>
            )}
            withPresets
            showExtraFooter
            onApply={onApply}
            open={openPicker}
            setOpen={(x) => setOpenPicker(x)}
            defaultValue={DEFAULT_RANGE}
          />
        </div>
        {!isPatient && (
          <SearchPatientsByType
            typeOptions={options}
            defaultTypeValue={DEFAULT_FILTER.value}
            dataOptions={dataOptions ?? []}
            typeValue={searchKey}
            searchValue={searchValue}
            isLoading={isLoading}
            placeholder={placeholder}
            onClear={() => {
              setSearchValue('');
            }}
            onTypeChange={(v) => {
              setSearchValue('');
              setSearchKey(v);
            }}
            onSelect={onSelect}
            onSearch={(e) => {
              setSearchValue(e.target.value);
              const isGuid = searchKey === 'guid';
              if (isGuid && isValidGuid(e.target.value)) {
                debounceSearch({
                  searchKey,
                  searchValue: e.target.value,
                });
              } else if (!isGuid) {
                debounceSearch({
                  searchKey,
                  searchValue: e.target.value,
                });
              }
            }}
          />
        )}
      </div>
    </NavigationHeader>
  );
};
export default RpmCcmNavigationHeader;
